import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Plus, CheckCircle, XCircle, Clock, TreePalm, Download } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeaveManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [activeTab, setActiveTab] = useState('applications');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [balances, setBalances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [typeForm, setTypeForm] = useState({ code: '', name: '', leave_category: 'paid', annual_quota: 12, carry_forward_limit: 0, allow_negative_balance: false, is_active: true, effective_from: new Date().toISOString().split('T')[0] });
  const [applyForm, setApplyForm] = useState({ employee_id: '', leave_type_id: '', from_date: '', to_date: '', total_days: 1, reason: '', is_half_day: false });

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);
  const fetchApplications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/leave/applications`, { withCredentials: true });
      setApplications(data);
    } catch (e) { console.error(e); }
  }, []);
  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees/lookup?limit=300`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, []);
  const fetchLeaveTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/leave/types?company_id=${selectedCompany}`, { withCredentials: true });
      setLeaveTypes(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => { if (selectedCompany) { fetchLeaveTypes(); fetchApplications(); fetchEmployees(); } }, [selectedCompany, fetchApplications, fetchEmployees, fetchLeaveTypes]);





  const fetchBalances = async (empId) => {
    try {
      const { data } = await axios.get(`${API_URL}/leave/balances/employee/${empId}`, { withCredentials: true });
      setBalances(data);
    } catch (e) { console.error(e); }
  };

  const handleCreateType = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/leave/types`, { ...typeForm, company_id: selectedCompany }, { withCredentials: true });
      toast.success('Leave type created');
      setShowTypeModal(false);
      fetchLeaveTypes();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Failed to create leave type'); }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/leave/applications`, applyForm, { withCredentials: true });
      toast.success('Leave application submitted');
      setShowApplyModal(false);
      fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply leave'); }
  };

  const handleApproval = async (appId, status) => {
    try {
      await axios.patch(`${API_URL}/leave/applications/${appId}`, { status }, { withCredentials: true });
      toast.success(`Leave ${status}`);
      fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAccrual = async () => {
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    try {
      const { data } = await axios.post(`${API_URL}/leave/accrual/run?company_id=${selectedCompany}&month=${month}&year=${year}`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Accrual failed'); }
  };

  const statusColors = {
    pending_manager: 'bg-amber-100 text-amber-700', pending_hr: 'bg-orange-100 text-orange-700',
    approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };

  return (
    <MainLayout>
      <div data-testid="leave-management-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leave Management</h1>
            <p className="text-sm text-slate-500 mt-1">Manage leave types, applications, and balances</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="run-accrual-btn" onClick={handleAccrual} className="text-sm text-[#0052FF] hover:underline font-medium">Run Accrual</button>
            <button data-testid="leave-export-btn" onClick={() => {
              const exportData = activeTab === 'applications' ? applications.map(a => ({
                Employee_ID: a.employee_id, Leave_Type: a.leave_type_id, From: a.from_date, To: a.to_date, Days: a.total_days, Status: a.status, Reason: a.reason
              })) : activeTab === 'balances' ? balances.map(b => ({
                Employee_ID: b.employee_id, Leave_Type: b.leave_type_id, Opening: b.opening_balance, Accrued: b.accrued, Used: b.used, Available: b.available_balance
              })) : leaveTypes.map(t => ({
                Code: t.code, Name: t.name, Annual_Quota: t.annual_quota, Carry_Forward: t.carry_forward_limit, Active: t.is_active
              }));
              if (exportData.length === 0) { toast.error('No data to export'); return; }
              downloadCSV(exportData, `leave_${activeTab}.csv`);
              toast.success(`${activeTab} exported`);
            }} className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          </div>
        </div>

        <div className="flex space-x-1 bg-slate-100 rounded-lg p-1">
          {['applications', 'leave-types', 'balances'].map((tab) => (
            <button key={tab} data-testid={`tab-${tab}`} onClick={() => setActiveTab(tab)}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {tab === 'leave-types' ? 'Leave Types' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {activeTab === 'applications' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-800">Leave Applications</h2>
              <button data-testid="apply-leave-btn" onClick={() => setShowApplyModal(true)} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]">
                <Plus className="w-4 h-4" /><span>Apply Leave</span>
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Employee</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">From</th>
                <th className="px-4 py-3 text-left font-medium">To</th>
                <th className="px-4 py-3 text-left font-medium">Days</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {applications.map((a) => (
                  <tr key={a.id} data-testid={`leave-app-row-${a.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-800">{a.employee_id}</td>
                    <td className="px-4 py-3 text-slate-600">{a.leave_type_id}</td>
                    <td className="px-4 py-3 text-slate-600">{a.from_date}</td>
                    <td className="px-4 py-3 text-slate-600">{a.to_date}</td>
                    <td className="px-4 py-3">{a.total_days}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[a.status] || 'bg-slate-100'}`}>{a.status?.replace('_', ' ')}</span></td>
                    <td className="px-4 py-3">
                      {(a.status === 'pending_manager' || a.status === 'pending_hr') && (
                        <div className="flex items-center space-x-1">
                          <button data-testid={`approve-leave-${a.id}`} onClick={() => handleApproval(a.id, 'approved')} className="text-green-600 hover:text-green-800"><CheckCircle className="w-4 h-4" /></button>
                          <button data-testid={`reject-leave-${a.id}`} onClick={() => handleApproval(a.id, 'rejected')} className="text-red-600 hover:text-red-800"><XCircle className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {applications.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No leave applications</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'leave-types' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-800">Leave Types</h2>
              <button data-testid="add-leave-type-btn" onClick={() => setShowTypeModal(true)} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]">
                <Plus className="w-4 h-4" /><span>Add Type</span>
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Code</th>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Category</th>
                <th className="px-4 py-3 text-left font-medium">Annual Quota</th>
                <th className="px-4 py-3 text-left font-medium">Carry Forward</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {leaveTypes.map((lt) => (
                  <tr key={lt.id} data-testid={`leave-type-row-${lt.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-800">{lt.code}</td>
                    <td className="px-4 py-3 text-slate-700">{lt.name}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{lt.leave_category}</td>
                    <td className="px-4 py-3">{lt.annual_quota}</td>
                    <td className="px-4 py-3">{lt.carry_forward_limit || lt.max_carry_forward || 0}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${lt.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>{lt.is_active ? 'Active' : 'Inactive'}</span></td>
                  </tr>
                ))}
                {leaveTypes.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No leave types configured</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'balances' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Select Employee</label>
              <select data-testid="balance-employee-select" onChange={(e) => { if (e.target.value) fetchBalances(e.target.value); }} className="w-full max-w-md border border-slate-300 rounded-lg px-3 py-2 text-sm">
                <option value="">Select...</option>{employees.map((emp) => <option key={emp.id} value={emp.id}>{`${emp.personal_details?.first_name} ${emp.personal_details?.last_name} (${emp.employment_details?.employee_code})`}</option>)}
              </select>
            </div>
            {balances.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {balances.map((b, i) => (
                  <div key={i} data-testid={`balance-card-${i}`} className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-slate-800">{b.leave_type_name}</h3>
                      <span className="text-xs text-slate-400 font-mono">{b.leave_type_code}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-blue-50 p-2 rounded"><p className="text-blue-500">Accrued</p><p className="font-bold text-blue-700">{b.accrued || 0}</p></div>
                      <div className="bg-red-50 p-2 rounded"><p className="text-red-500">Availed</p><p className="font-bold text-red-700">{b.availed || 0}</p></div>
                      <div className="bg-green-50 p-2 rounded"><p className="text-green-500">Balance</p><p className="font-bold text-green-700">{b.closing_balance || 0}</p></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Type Modal */}
        {showTypeModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowTypeModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">New Leave Type</h3>
              <form onSubmit={handleCreateType} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Code</label><input data-testid="ltype-code-input" value={typeForm.code} onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toUpperCase() })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Name</label><input data-testid="ltype-name-input" value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                    <select data-testid="ltype-category-select" value={typeForm.leave_category} onChange={(e) => setTypeForm({ ...typeForm, leave_category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white" required>
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                      <option value="compensatory">Compensatory</option>
                    </select></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Effective From *</label>
                    <input data-testid="ltype-effective-input" type="date" value={typeForm.effective_from} onChange={(e) => setTypeForm({ ...typeForm, effective_from: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Annual Quota</label><input type="number" value={typeForm.annual_quota} onChange={(e) => setTypeForm({ ...typeForm, annual_quota: parseInt(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Carry Forward Limit</label><input type="number" value={typeForm.carry_forward_limit} onChange={(e) => setTypeForm({ ...typeForm, carry_forward_limit: parseInt(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                </div>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center space-x-2"><input type="checkbox" checked={typeForm.allow_negative_balance} onChange={(e) => setTypeForm({ ...typeForm, allow_negative_balance: e.target.checked })} /><span className="text-sm text-slate-700">Allow Negative Balance</span></label>
                  <label className="flex items-center space-x-2"><input type="checkbox" checked={typeForm.is_active} onChange={(e) => setTypeForm({ ...typeForm, is_active: e.target.checked })} /><span className="text-sm text-slate-700">Active</span></label>
                </div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowTypeModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-leave-type-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Save</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Apply Modal */}
        {showApplyModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setShowApplyModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Apply Leave</h3>
              <form onSubmit={handleApply} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Employee</label>
                  <select data-testid="apply-employee-select" value={applyForm.employee_id} onChange={(e) => setApplyForm({ ...applyForm, employee_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                    <option value="">Select</option>{employees.map((emp) => <option key={emp.id} value={emp.id}>{`${emp.personal_details?.first_name} ${emp.personal_details?.last_name}`}</option>)}
                  </select></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Leave Type</label>
                  <select data-testid="apply-type-select" value={applyForm.leave_type_id} onChange={(e) => setApplyForm({ ...applyForm, leave_type_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                    <option value="">Select</option>{leaveTypes.map((lt) => <option key={lt.id} value={lt.id}>{`${lt.name} (${lt.code})`}</option>)}
                  </select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">From</label><input type="date" value={applyForm.from_date} onChange={(e) => setApplyForm({ ...applyForm, from_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">To</label><input type="date" value={applyForm.to_date} onChange={(e) => setApplyForm({ ...applyForm, to_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Total Days</label><input type="number" step="0.5" value={applyForm.total_days} onChange={(e) => setApplyForm({ ...applyForm, total_days: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div className="flex items-end pb-1"><label className="flex items-center space-x-2"><input type="checkbox" checked={applyForm.is_half_day} onChange={(e) => setApplyForm({ ...applyForm, is_half_day: e.target.checked, total_days: e.target.checked ? 0.5 : 1 })} /><span className="text-sm text-slate-700">Half Day</span></label></div>
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Reason</label><textarea value={applyForm.reason} onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} /></div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowApplyModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="submit-leave-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC]">Submit</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default LeaveManagement;
